import React from 'react';
import { Calendar, Package, Truck, CheckCircle, RefreshCw, Eye, AlertTriangle, RotateCcw, Tag, CreditCard, Undo2, Star, Clock, ShoppingBag, X, AlertCircle, Printer, Zap, Shield } from 'lucide-react';
import OrderStatusBadge from './OrderStatusBadge';

const OrderCard = ({
  order,
  onClick,
  onReorder,
  onReturnRequest,
  onReview,
  onCancelRequest
}) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusIcon = (status) => {
    // Normalize status to handle case variations
    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '_');

    switch (normalizedStatus) {
      case 'placed':
        return <ShoppingBag className="w-5 h-5" />;
      case 'processing':
        return <ShoppingBag className="w-5 h-5" />;
      case 'printing_started':
      case 'printing started':
        return <Printer className="w-5 h-5" />;
      case 'production_completed':
      case 'production completed':
        return <Zap className="w-5 h-5" />;
      case 'quality_check':
      case 'quality check':
        return <Shield className="w-5 h-5" />;
      case 'packaging':
        return <ShoppingBag className="w-5 h-5" />;
      case 'shipped':
        return <Truck className="w-5 h-5" />;
      case 'delivered':
        return <CheckCircle className="w-5 h-5" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5" />;
      case 'return_requested':
      case 'return requested':
        return <RotateCcw className="w-5 h-5" />;
      case 'return_approved':
      case 'return approved':
        return <RotateCcw className="w-5 h-5" />;
      case 'return_rejected':
      case 'return rejected':
        return <AlertCircle className="w-5 h-5" />;
      case 'returned':
        return <RotateCcw className="w-5 h-5" />;
      case 'inspection_pending':
      case 'inspection pending':
        return <Shield className="w-5 h-5" />;
      case 'partial_refund':
      case 'partial refund':
        return <RefreshCw className="w-5 h-5" />;
      case 'replacement_processing':
      case 'replacement processing':
        return <RefreshCw className="w-5 h-5" />;
      case 'refund_processing':
      case 'refund processing':
        return <RefreshCw className="w-5 h-5" />;
      case 'cancellation_requested':
      case 'cancellation requested':
      case 'cancel_requested':
      case 'cancel requested':
        return <AlertCircle className="w-5 h-5" />;
      case 'cancelled':
      case 'cancelled_(not_refunded)':
      case 'cancelled (not refunded)':
        return <X className="w-5 h-5" />;
      case 'returned_(no_refund)':
      case 'returned (no refund)':
      case 'returned_no_refund':
        return <X className="w-5 h-5" />;
      case 'refund':
      case 'refunded':
        return <CheckCircle className="w-5 h-5" />;
      case 'rejected':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return <Package className="w-5 h-5" />;
    }
  };

  const getProgressConfig = (status) => {
    // Normalize status to handle case variations
    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '_');

    switch (normalizedStatus) {
      case 'placed':
        return { width: 'w-1/4', color: 'bg-teal-500', label: 'Order Placed' };
      case 'processing':
        return { width: 'w-2/4', color: 'bg-blue-500', label: 'Awaiting Process' };
      case 'printing_started':
      case 'printing started':
        return { width: 'w-2/4', color: 'bg-indigo-500', label: 'Awaiting Process' };
      case 'production_completed':
      case 'production completed':
        return { width: 'w-2/4', color: 'bg-cyan-500', label: 'Awaiting Process' };
      case 'quality_check':
      case 'quality check':
        return { width: 'w-2/4', color: 'bg-yellow-500', label: 'Awaiting Process' };
      case 'packaging':
        return { width: 'w-2/4', color: 'bg-amber-500', label: 'Awaiting Process' };
      case 'shipped':
        return { width: 'w-3/4', color: 'bg-violet-500', label: 'Shipped' };
      case 'delivered':
        return { width: 'w-full', color: 'bg-emerald-500', label: 'Delivered' };
      case 'completed':
        return { width: 'w-full', color: 'bg-emerald-600', label: 'Completed' };
      case 'return_requested':
      case 'return requested':
        return { width: 'w-full', color: 'bg-sky-500', label: 'Return Requested' };
      case 'return_approved':
      case 'return approved':
        return { width: 'w-full', color: 'bg-teal-400', label: 'Return Approved' };
      case 'return_rejected':
      case 'return rejected':
        return { width: 'w-full', color: 'bg-red-500', label: 'Return Rejected' };
      case 'returned':
        return { width: 'w-full', color: 'bg-orange-500', label: 'Received Product' };
      case 'inspection_pending':
      case 'inspection pending':
        return { width: 'w-full', color: 'bg-purple-400', label: 'Inspection' };
      case 'partial_refund':
      case 'partial refund':
        return { width: 'w-full', color: 'bg-lime-500', label: 'Partial Refund' };
      case 'replacement_processing':
      case 'replacement processing':
        return { width: 'w-full', color: 'bg-sky-400', label: 'Replacement' };
      case 'refund_processing':
      case 'refund processing':
        return { width: 'w-full', color: 'bg-indigo-400', label: 'Refund Processing' };
      case 'cancellation_requested':
      case 'cancellation requested':
      case 'cancel_requested':
      case 'cancel requested':
        return { width: 'w-2/4', color: 'bg-rose-400', label: 'Cancel Requested' };
      case 'cancelled':
      case 'cancelled_(not_refunded)':
      case 'cancelled (not refunded)':
        return { width: 'w-1/4', color: 'bg-red-500', label: 'Cancelled' };
      case 'returned_(no_refund)':
      case 'returned (no refund)':
      case 'returned_no_refund':
        return { width: 'w-full', color: 'bg-red-700', label: 'No Refund' };
      case 'refund':
      case 'refunded':
        return { width: 'w-full', color: 'bg-green-500', label: 'Refunded' };
      case 'rejected':
        return { width: 'w-1/4', color: 'bg-rose-600', label: 'Rejected' };
      default:
        return { width: 'w-1/4', color: 'bg-gray-500', label: 'Unknown' };
    }
  };

  const canReorder = ['Delivered', 'Completed', 'Cancelled', 'Refund', 'Refunded', 'Rejected', 'Returned (No Refund)', 'returned_no_refund'].includes(order.status);
  const canReturn = order.status === 'Delivered' || order.status === 'Completed';
  const canReview = ['Delivered', 'Completed', 'Return Requested', 'return_requested', 'Return Approved', 'return_approved', 'Returned', 'Refund', 'Refunded', 'Partial Refund', 'partial_refund'].includes(order.status);
  const canCancel = ['Placed', 'Processing'].includes(order.status);

  const progressConfig = getProgressConfig(order.status);

  return (
    <article className="bg-white bg-opacity-60 rounded-xl border border-orange-200 shadow-sm hover:shadow-2xl hover:shadow-orange-200/50 transition-all duration-300 hover:-translate-y-2 overflow-hidden relative z-0">
      {/* Order Header */}
      <div className="px-4 md:px-6 py-2 md:py-2 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          {/* Order Info */}
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2">
              <h3 className="text-lg font-bold text-gray-800">
                Order {order.orderNumber}
              </h3>
              <div className="flex flex-wrap gap-2">
                {order.hasUnavailableItems && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                    <AlertTriangle className="w-3 h-3" />
                    Some items unavailable
                  </span>
                )}
                {order.promoCode && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                    <Tag className="w-3 h-3" />
                    {order.promoCode}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>Ordered {formatDate(order.date)}</span>
              </div>

              <div className="flex items-center gap-1">
                <Package className="w-4 h-4" />
                <span>{order.itemCount} item{order.itemCount !== 1 ? 's' : ''}</span>
              </div>

              {order.trackingNumber && (
                <div className="flex items-center gap-1">
                  <CreditCard className="w-4 h-4" />
                  <span className="font-mono text-xs">{order.trackingNumber}</span>
                </div>
              )}
            </div>

            {/* Delivery Info */}
            {order.estimatedDelivery && (
              <div className="mt-2 text-sm">
                {order.status === 'Delivered' && order.actualDelivery ? (
                  <span className="text-green-600 font-medium">
                    Delivered on {formatDate(order.actualDelivery)}
                  </span>
                ) : order.status === 'Completed' ? (
                  <span className="text-emerald-600 font-medium">
                    Order completed
                  </span>
                ) : order.status === 'Cancelled' ? (
                  <span className="text-red-600 font-medium">
                    Order cancelled
                  </span>
                ) : order.status === 'Refund' || order.status === 'Refunded' ? (
                  <span className="text-indigo-600 font-medium">
                    Order refunded
                  </span>
                ) : order.status === 'Rejected' ? (
                  <span className="text-red-600 font-medium">
                    Order rejected
                  </span>
                ) : order.status === 'Return Requested' || order.status === 'return_requested' ? (
                  <span className="text-amber-600 font-medium">
                    Return requested
                  </span>
                ) : order.status === 'Return Approved' || order.status === 'return_approved' ? (
                  <span className="text-teal-600 font-medium">
                    Return approved
                  </span>
                ) : order.status === 'Return Rejected' || order.status === 'return_rejected' ? (
                  <span className="text-red-600 font-medium">
                    Return rejected
                  </span>
                ) : order.status === 'Cancellation Requested' || order.status === 'cancellation_requested' || order.status === 'cancel_requested' ? (
                  <span className="text-red-600 font-medium">
                    Cancellation requested
                  </span>
                ) : order.status === 'Returned' ? (
                  <span className="text-orange-600 font-medium">
                    Product received
                  </span>
                ) : order.status === 'Returned (No Refund)' || order.status === 'returned_no_refund' ? (
                  <span className="text-red-600 font-medium">
                    Returned - No refund
                  </span>
                ) : order.status === 'Inspection Pending' || order.status === 'inspection_pending' ? (
                  <span className="text-purple-600 font-medium">
                    Under inspection
                  </span>
                ) : order.status === 'Partial Refund' || order.status === 'partial_refund' ? (
                  <span className="text-lime-600 font-medium">
                    Partial refund processed
                  </span>
                ) : order.status === 'Replacement Processing' || order.status === 'replacement_processing' ? (
                  <span className="text-sky-600 font-medium">
                    Replacement in progress
                  </span>
                ) : order.status === 'Refund Processing' || order.status === 'refund_processing' ? (
                  <span className="text-indigo-600 font-medium">
                    Refund processing
                  </span>
                ) : order.status === 'Printing Started' ? (
                  <span className="text-indigo-600 font-medium">
                    Printing in progress
                  </span>
                ) : order.status === 'Production Completed' ? (
                  <span className="text-cyan-600 font-medium">
                    Production completed
                  </span>
                ) : order.status === 'Quality Check' || order.status === 'quality_check' ? (
                  <span className="text-yellow-600 font-medium">
                    Quality check in progress
                  </span>
                ) : order.status === 'Placed' ? (
                  <span className="text-teal-600 font-medium">
                    Order placed successfully
                  </span>
                ) : (
                  <span className="text-blue-600 font-medium">
                    Expected delivery: {formatDate(order.estimatedDelivery)}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Order Total and Status */}
          <div className="text-right">
            <div className="text-2xl font-bold text-teal-700 mb-2">
              ₹{order.total.toFixed(2)}
              {order.promoDiscount > 0 && (
                <div className="text-sm text-green-600 font-normal">
                  Saved ₹{order.promoDiscount.toFixed(2)}
                </div>
              )}
            </div>
            <div className="flex flex-row gap-2">
              <OrderStatusBadge status={order.status} type="order" />
              <OrderStatusBadge
                status={order.paymentStatus ? order.paymentStatus.toLowerCase() : 'pending'}
                type="payment"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Order Items Preview */}
      <div className="p-4 md:p-6">
        <div className="flex flex-wrap gap-3 mb-4">
          {order.items.slice(0, 3).map((item, index) => (
            <div key={item.id} className="relative">
              <img
                src={item.image}
                alt={item.name}
                className={`w-16 h-16 md:w-20 md:h-20 object-cover rounded-lg border border-gray-200 ${
                  item.isUnavailable ? 'opacity-50 grayscale' : ''
                }`}
                loading="lazy"
              />
              {item.quantity > 1 && (
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-teal-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {item.quantity}
                </div>
              )}
              {item.isUnavailable && (
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                  <X className="w-2 h-2 text-white" />
                </div>
              )}
              {item.customization && !item.isUnavailable && Object.keys(item.customization).length > 0 && (
                <div className="absolute -bottom-1 -left-1 w-4 h-4 bg-orange-400 rounded-full flex items-center justify-center" title="Customized item">
                  <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
          ))}

          {order.items.length > 3 && (
            <div className="w-16 h-16 md:w-20 md:h-20 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
              <span className="text-sm font-medium text-gray-600">
                +{order.items.length - 3}
              </span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 text-sm font-medium"
          >
            <Eye className="w-4 h-4" />
            View Details
          </button>

          {canReorder && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onReorder();
              }}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Reorder
            </button>
          )}

          {canReturn && onReturnRequest && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onReturnRequest(order);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 text-sm font-medium"
            >
              <Undo2 className="w-4 h-4" />
              Return
            </button>
          )}

          {canCancel && onCancelRequest && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCancelRequest(order);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 text-sm font-medium"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          )}

          {canReview && onReview && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onReview(order);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 text-sm font-medium"
            >
              <Star className="w-4 h-4" />
              Review
            </button>
          )}
        </div>

        {/* Progress Bar for All Orders */}
        <div className="space-y-2">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600">Order Progress</span>
            <span className={`font-medium ${
              order.status === 'Delivered' || order.status === 'Completed' ? 'text-emerald-600' :
              order.status === 'Refund' || order.status === 'Refunded' ? 'text-green-600' :
              order.status === 'Return Requested' || order.status === 'return_requested' ? 'text-sky-600' :
              order.status === 'Return Approved' || order.status === 'return_approved' ? 'text-teal-600' :
              order.status === 'Return Rejected' || order.status === 'return_rejected' ? 'text-red-600' :
              order.status === 'Cancellation Requested' || order.status === 'cancellation_requested' || order.status === 'cancel_requested' ? 'text-red-600' :
              order.status === 'Returned' ? 'text-orange-600' :
              order.status === 'Returned (No Refund)' || order.status === 'returned_no_refund' ? 'text-red-600' :
              order.status === 'Cancelled' || order.status === 'Cancelled (Not Refunded)' ? 'text-red-600' :
              order.status === 'Rejected' ? 'text-rose-600' :
              order.status === 'Shipped' ? 'text-violet-600' :
              order.status === 'Packaging' ? 'text-amber-600' :
              order.status === 'Quality Check' || order.status === 'quality_check' ? 'text-yellow-600' :
              order.status === 'Production Completed' ? 'text-cyan-600' :
              order.status === 'Printing Started' ? 'text-indigo-600' :
              order.status === 'Processing' ? 'text-blue-600' :
              order.status === 'Placed' ? 'text-teal-600' :
              order.status === 'Inspection Pending' || order.status === 'inspection_pending' ? 'text-purple-600' :
              order.status === 'Partial Refund' || order.status === 'partial_refund' ? 'text-lime-600' :
              order.status === 'Replacement Processing' || order.status === 'replacement_processing' ? 'text-sky-600' :
              order.status === 'Refund Processing' || order.status === 'refund_processing' ? 'text-indigo-600' :
              'text-gray-600'
            }`}>
              {progressConfig.label}
            </span>
          </div>

          <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${progressConfig.color} ${progressConfig.width}`}
            />
          </div>

          {/* <div className="flex justify-between text-xs text-gray-600">
            <span className={!['Cancelled', 'Rejected', 'Cancelled (Not Refunded)', 'Cancellation Requested', 'cancellation_requested', 'cancel_requested'].includes(order.status) ? 'text-gray-800 font-medium' : ''}>Ordered</span>
            <span className={['Processing','Printing Started','Production Completed','Quality Check','quality_check','Packaging','Shipped','Delivered','Completed','Return Requested','return_requested','Return Approved','return_approved','Returned','Refund','Refunded','Partial Refund','partial_refund','Replacement Processing','replacement_processing','Refund Processing','refund_processing'].includes(order.status) ? 'text-gray-800 font-medium' : ''}>Awaiting Process</span>
            <span className={['Shipped','Delivered','Completed','Return Requested','return_requested','Return Approved','return_approved','Returned','Refund','Refunded','Partial Refund','partial_refund','Replacement Processing','replacement_processing','Refund Processing','refund_processing'].includes(order.status) ? 'text-gray-800 font-medium' : ''}>Shipped</span>
            <span className={['Delivered','Completed','Return Requested','return_requested','Return Approved','return_approved','Returned','Refund','Refunded','Partial Refund','partial_refund','Replacement Processing','replacement_processing','Refund Processing','refund_processing'].includes(order.status) ? 'text-gray-800 font-medium' : ''}>
              {order.status === 'Refund' || order.status === 'Refunded' ? 'Refunded' :
               order.status === 'Return Requested' || order.status === 'return_requested' ? 'Return Req.' :
               order.status === 'Return Approved' || order.status === 'return_approved' ? 'Return App.' :
               order.status === 'Return Rejected' || order.status === 'return_rejected' ? 'Return Rej.' :
               order.status === 'Cancellation Requested' || order.status === 'cancellation_requested' || order.status === 'cancel_requested' ? 'Cancel Req.' :
               order.status === 'Returned' ? 'Received' :
               order.status === 'Returned (No Refund)' || order.status === 'returned_no_refund' ? 'No Refund' :
               order.status === 'Completed' ? 'Completed' :
               order.status === 'Partial Refund' || order.status === 'partial_refund' ? 'Part. Refund' :
               order.status === 'Replacement Processing' || order.status === 'replacement_processing' ? 'Replacement' :
               order.status === 'Refund Processing' || order.status === 'refund_processing' ? 'Refund Proc.' :
               'Delivered'}
            </span>
          </div> */}
        </div>
      </div>
    </article>
  );
};

export default OrderCard;
